import { useMemo, useState } from 'react';
import { FormControlLabel, Paper, Skeleton, Stack, Switch, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import ChartMixed from 'src/sections/charts/chart-mixed';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { brandedCampaignsVisibility } from 'src/utils/branded-campaigns';
import { CHART_HEIGHT, CHART_INNER_HEIGHT, PERFORMANCE_COLORS, formatLabels } from './constants';

interface FunnelPerformanceChartProps {
  kpisData: any;
  compareData: any;
  kpisError: any;
  isFetchingKpis: boolean;
}

export default function FunnelPerformanceChart({
  kpisData,
  compareData,
  kpisError,
  isFetchingKpis,
}: FunnelPerformanceChartProps) {
  const { t, currentLang } = useLocales();
  const filterStore = useSelector((state: any) => state.filters);
  const workspace: any = useCurrentWorkspace();
  const [showLabels, setShowLabels] = useState(false);
  const [showEvolution, setShowEvolution] = useState(false);

  // Les séries Shopping / PMax n'apparaissent que si les campagnes brandées
  // correspondantes sont configurées dans le workspace (la légende suit les séries).
  const { showShopping, showPmax } = brandedCampaignsVisibility(workspace);

  const performanceChart = useMemo(() => {
    const chart: any[] = [];
    const hasData = (values: number[]) => values.some((value) => value > 0);
    const searchData =
      kpisData?.time_series_data?.map((item: any) => item?.adCostSearch ?? 0) ?? [];
    const shoppingData =
      kpisData?.time_series_data?.map((item: any) => item?.adCostShopping ?? 0) ?? [];
    const pmaxData =
      kpisData?.time_series_data?.map((item: any) => item?.adCostPmax ?? 0) ?? [];

    chart.push({
      name: t('dashboard.global.blended_ctr'),
      type: 'line',
      data: kpisData?.time_series_data?.map((item: any) => item?.blendedCtr ?? 0) ?? [],
      yAxisIndex: 1,
      color: PERFORMANCE_COLORS.blendedCtr,
    });

    if (hasData(searchData)) {
      chart.push({
        name: t('dashboard.brandTraffic.searchAdCost'),
        type: 'bar',
        data: searchData,
        yAxisIndex: 0,
        color: PERFORMANCE_COLORS.searchAdCost,
        group: 'adCost',
      });
    }

    if (showShopping && hasData(shoppingData)) {
      chart.push({
        name: t('dashboard.brandTraffic.shoppingAdCost'),
        type: 'bar',
        data: shoppingData,
        yAxisIndex: 0,
        color: PERFORMANCE_COLORS.shoppingAdCost,
        group: 'adCost',
      });
    }

    if (showPmax && hasData(pmaxData)) {
      chart.push({
        name: t('dashboard.global.pmax_ad_cost'),
        type: 'bar',
        data: pmaxData,
        yAxisIndex: 0,
        color: PERFORMANCE_COLORS.pmaxAdCost,
        group: 'adCost',
      });
    }

    chart.push({
      name: t('dashboard.global.blended_cpc'),
      type: 'line',
      data: kpisData?.time_series_data?.map((item: any) => item?.blendedCpc ?? 0) ?? [],
      yAxisIndex: 1,
      color: PERFORMANCE_COLORS.blendedCpc,
    });

    return chart;
  }, [kpisData, showShopping, showPmax, t]);

  // Utilise exactement les séries affichées, dans le même ordre.
  // Certaines séries de coûts n'existent pas selon le workspace / les données.
  const comparisonPerformanceChart = useMemo(() => {
    const fieldsByName: Record<string, string> = {
      [t('dashboard.global.blended_ctr')]: 'blendedCtr',
      [t('dashboard.brandTraffic.searchAdCost')]: 'adCostSearch',
      [t('dashboard.brandTraffic.shoppingAdCost')]: 'adCostShopping',
      [t('dashboard.global.pmax_ad_cost')]: 'adCostPmax',
      [t('dashboard.global.blended_cpc')]: 'blendedCpc',
    };

    return performanceChart.map((serie) => ({
      name: serie.name,
      data:
        compareData?.time_series_data?.map(
          (item: any) => item?.[fieldsByName[serie.name]] ?? 0
        ) ?? [],
    }));
  }, [compareData, performanceChart, t]);

  // Disable the bar border radius when the top stacked segment carries no spend.
  // A $0 top segment renders as a sub-radius stub that ApexCharts rounds on the
  // wrong (bottom) edge; a square top is cleaner there. Rounding stays on as
  // soon as Shopping (or PMax) has spend and thus a real, roundable height.
  const barBorderRadius = useMemo(() => {
    const isSplit = workspace?.template === 'E-commerce' || workspace?.shoppingIncluded;
    if (!isSplit) return 4;
    const shopping = kpisData?.time_series_data?.map((item: any) => item?.adCostShopping ?? 0) ?? [];
    const pmax = kpisData?.time_series_data?.map((item: any) => item?.adCostPmax ?? 0) ?? [];
    const hasPmaxData = kpisData?.time_series_data?.every(
      (item: any) => item?.adCostPmax !== undefined
    );
    // Une série masquée (campagnes brandées non configurées) ne peut pas porter le radius.
    const pmaxShown =
      showPmax && hasPmaxData && workspace?.shoppingIncluded === false && pmax.some((value: number) => value > 0);
    const shoppingShown = showShopping && shopping.some((value: number) => value > 0);
    return shoppingShown || pmaxShown ? 4 : 0;
  }, [kpisData, workspace?.template, workspace?.shoppingIncluded, showShopping, showPmax]);

  const customLabels = formatLabels(
    kpisData?.time_series_data ?? [],
    filterStore.timestep,
    currentLang.value
  );

  return (
    <Paper
      sx={{
        backgroundColor: 'common.white',
        boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
        border: '1px solid #eaeaea',
        height: `${CHART_HEIGHT}px`,
        borderRadius: '16px',
      }}
    >
      {isFetchingKpis || (!kpisData && !kpisError) ? (
        <Skeleton
          width="100%"
          height="100%"
          style={{ borderRadius: 10 }}
          variant="rectangular"
          animation="wave"
        >
          <>
            <Iconify py="auto" icon="eva:checkmark-circle-2-fill" color="success.main" />
            <Typography color="success.main">Active</Typography>
          </>
        </Skeleton>
      ) : (
        <div style={{ padding: '24px' }}>
          <Stack spacing={2} sx={{ mb: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <Typography
                  variant="overline"
                  sx={{ color: '#919eab', display: 'block', lineHeight: 1.4 }}
                >
                  {t('dashboard.brandTraffic.trafficCost')}
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2, color: '#1c252e' }}>
                  {t('dashboard.brandTraffic.blendedCtrAndAdCost')}
                </Typography>
              </div>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showLabels}
                      onChange={(event) => setShowLabels(event.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#212B36', opacity: 1 },
                      }}
                    />
                  }
                  label={t('dashboard.brandTraffic.showLabels')}
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': { fontSize: 13, color: '#637381' },
                  }}
                />
                {filterStore?.compare &&
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showEvolution}
                        onChange={(event) => setShowEvolution(event.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#212B36', opacity: 1 },
                        }}
                      />
                    }
                    label="Show evolution"
                    sx={{
                      m: 0,
                      '& .MuiFormControlLabel-label': { fontSize: 13, color: '#637381' },
                    }}
                  />
                }
              </Stack>
            </div>
          </Stack>
          <ChartMixed
            series={kpisData ? performanceChart : []}
            labels={customLabels}
            height={CHART_INNER_HEIGHT}
            showDataLabels={showLabels}
            comparisonSeries={compareData ? comparisonPerformanceChart : []}
            showEvolution={showEvolution}
            barBorderRadius={barBorderRadius}
          />
        </div>
      )}
    </Paper>
  );
}